/**
 * API input contracts, shared by server validation and web forms.
 * Output shapes are inferred server-side; only inputs live here.
 */
import { z } from 'zod';
import { COMPONENT_KINDS, CRITICALITIES, ENVIRONMENT_KINDS, ENVIRONMENT_LINK_KINDS, ERROR_LEVELS, FINDING_STATUSES, INCIDENT_EVENT_KINDS, INCIDENT_SEVERITIES, ISSUE_STATUSES, ONBOARDING_STEPS, PROJECT_STATUSES, VERSIONING_SCOPES, } from './domain.js';
import { ENVIRONMENT_ACTIONS, GLOBAL_ROLES, PROJECT_ACTIONS } from './permissions.js';
/**
 * PATCH contract from a create schema: every field optional and, crucially, no
 * default values — otherwise an absent field would silently overwrite stored data.
 */
export function patchSchema(schema) {
    const shape = Object.fromEntries(Object.entries(schema.shape).map(([k, v]) => [k, (v instanceof z.ZodDefault ? v.removeDefault() : v).optional()]));
    return z.object(shape);
}
const slug = z
    .string()
    .min(2)
    .max(48)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lettres minuscules, chiffres et tirets uniquement');
const httpUrl = z
    .string()
    .url()
    .refine((u) => /^https?:\/\//i.test(u), 'URL http(s) attendue')
    .refine((u) => {
    // F05: never store credentials or durable tokens in a link.
    const parsed = new URL(u);
    if (parsed.username || parsed.password)
        return false;
    const suspicious = ['token', 'access_token', 'password', 'pwd', 'secret', 'apikey', 'api_key', 'key'];
    return ![...parsed.searchParams.keys()].some((k) => suspicious.includes(k.toLowerCase()));
}, 'Une URL ne doit contenir ni identifiant, ni mot de passe, ni jeton');
export const docLinkSchema = z.object({ label: z.string().min(1).max(80), url: httpUrl });
export const projectCreateSchema = z.object({
    slug,
    name: z.string().min(1).max(120),
    description: z.string().max(2000).default(''),
    ownerUserId: z.string().uuid().nullable().optional(),
    tags: z.array(z.string().min(1).max(32)).max(20).default([]),
    criticality: z.enum(CRITICALITIES).default('medium'),
    status: z.enum(PROJECT_STATUSES).default('development'),
    docLinks: z.array(docLinkSchema).max(20).default([]),
    versioningScope: z.enum(VERSIONING_SCOPES).default('global'),
    rpoMinutes: z.number().int().positive().nullable().optional(),
    rtoMinutes: z.number().int().positive().nullable().optional(),
    templateId: z.string().uuid().optional(),
});
export const projectUpdateSchema = patchSchema(projectCreateSchema.omit({ slug: true, templateId: true }));
export const onboardingUpdateSchema = z.object({
    completed: z.array(z.enum(ONBOARDING_STEPS)),
    current: z.enum(ONBOARDING_STEPS),
    skipped: z.array(z.enum(ONBOARDING_STEPS)).optional(),
});
export const environmentLinkSchema = z.object({
    kind: z.enum(ENVIRONMENT_LINK_KINDS),
    label: z.string().min(1).max(80),
    url: httpUrl,
});
export const environmentCreateSchema = z.object({
    name: z.string().min(1).max(40),
    kind: z.enum(ENVIRONMENT_KINDS),
    links: z.array(environmentLinkSchema).max(30).default([]),
    configKeys: z.array(z.string().min(1).max(120)).max(200).default([]),
    /** Recovery objectives for this environment (minutes); null = use the project's. */
    rpoMinutes: z.number().int().positive().nullable().optional(),
    rtoMinutes: z.number().int().positive().nullable().optional(),
});
export const environmentUpdateSchema = patchSchema(environmentCreateSchema);
export const componentCreateSchema = z.object({
    environmentId: z.string().uuid().nullable(),
    kind: z.enum(COMPONENT_KINDS),
    name: z.string().min(1).max(80),
    connectionId: z.string().uuid().nullable().optional(),
    /** Non-secret connector settings (e.g. database name). */
    config: z.record(z.string(), z.unknown()).default({}),
    declaredVersion: z.string().max(40).nullable().optional(),
    /** Witness checks run after a restore: read-only SQL returning one numeric value. */
    witnessQueries: z.array(z.object({ label: z.string().min(1).max(80), sql: z.string().min(1).max(500) })).max(20).default([]),
});
export const componentUpdateSchema = patchSchema(componentCreateSchema);
export const repositoryCreateSchema = z.object({
    provider: z.enum(['github', 'gitlab', 'bitbucket', 'local']),
    /** owner/name (GitHub, Bitbucket), group/project (GitLab), absolute path (local). */
    locator: z.string().min(1).max(500),
    defaultBranch: z.string().max(120).default('main'),
    subPath: z.string().max(300).default(''),
    connectionId: z.string().uuid().nullable().optional(),
    changelogPath: z.string().max(300).default('CHANGELOG.md'),
    /** Mandatory project documentation folder (journal, team, tasks). */
    docsPath: z.string().max(300).default('Doc'),
});
export const connectionCreateSchema = z.object({
    connector: z.string().min(1).max(40),
    name: z.string().min(1).max(80),
    projectId: z.string().uuid().nullable().optional(),
    config: z.record(z.string(), z.unknown()).default({}),
    /** Write-only. Encrypted into the vault on receipt and never returned. */
    secret: z.string().min(1).max(10_000).optional(),
    /** SSH/SFTP: the console generates an ed25519 key pair; only the public key is ever shown. */
    generateKey: z.boolean().optional(),
});
export const backupPolicySchema = z.object({
    environmentId: z.string().uuid(),
    name: z.string().min(1).max(80),
    componentIds: z.array(z.string().uuid()).min(1),
    cron: z.string().min(9).max(120),
    timezone: z.string().min(1).max(64).default('Europe/Paris'),
    windowMinutes: z.number().int().min(5).max(24 * 60).default(120),
    graceMinutes: z.number().int().min(0).max(24 * 60).default(60),
    destinationConnectionId: z.string().uuid(),
    retentionKeepLast: z.number().int().min(1).max(1000).default(7),
    retentionKeepDays: z.number().int().min(1).max(3650).default(30),
    encrypt: z.boolean().default(true),
    maxRetries: z.number().int().min(0).max(10).default(2),
    paused: z.boolean().default(false),
});
export const restorePlanSchema = z.object({
    backupId: z.string().uuid(),
    targetComponentId: z.string().uuid(),
});
export const restoreConfirmSchema = z.object({
    /** Must equal `<project-slug>/<environment-name>` for production targets. */
    typedConfirmation: z.string().optional(),
    acknowledgeNoPreBackup: z.boolean().default(false),
    acknowledgeWarnings: z.boolean().default(false),
});
export const monitorSchema = z.object({
    environmentId: z.string().uuid(),
    name: z.string().min(1).max(80),
    url: httpUrl,
    intervalSeconds: z.number().int().min(30).max(86_400).default(300),
    timeoutMs: z.number().int().min(500).max(60_000).default(10_000),
    failureThreshold: z.number().int().min(1).max(20).default(2),
    expectedStatus: z.number().int().min(100).max(599).default(200),
    checkCertificate: z.boolean().default(true),
});
export const grantSchema = z.object({
    userId: z.string().uuid(),
    environmentId: z.string().uuid().nullable(),
    actions: z.array(z.enum([...PROJECT_ACTIONS, ...ENVIRONMENT_ACTIONS])).min(1),
});
export const userCreateSchema = z.object({
    email: z.string().email().max(200),
    name: z.string().min(1).max(120),
    role: z.enum(GLOBAL_ROLES).exclude(['owner']),
    /** Optional: without a password the account signs in with GitHub or Google only. */
    password: z.string().min(12).max(200).optional(),
});
/** Sign-up link: optionally bound to one address, accepted through GitHub or Google. */
export const invitationCreateSchema = z.object({
    email: z.string().email().max(200).optional(),
    name: z.string().min(1).max(120).optional(),
    role: z.enum(GLOBAL_ROLES).exclude(['owner']),
    expiresInDays: z.number().int().min(1).max(30).default(7),
});
export const loginSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1).max(200),
});
export const mfaCodeSchema = z.object({ code: z.string().regex(/^\d{6}$/) });
/** Only safe characters reach a remote shell: versions, refs, hosts, paths. */
const shellSafe = /^[\w@%+=:,./-]+$/;
/** How the console deploys an environment through an SSH connection (F12). */
export const deployTargetSchema = z.object({
    connectionId: z.string().uuid(),
    /** Absolute path of the application on the server. */
    directory: z.string().min(2).max(300).regex(/^\/[\w@%+=:,./-]+$/, 'Chemin absolu sans espace ni caractère spécial'),
    /** git: fetch + checkout of the release ref; none: commands only (artefacts managed elsewhere). */
    source: z.enum(['git', 'none']).default('git'),
    /** Ref deployed for a version; {version} is replaced. */
    refTemplate: z.string().min(1).max(80).regex(/^[\w{}.\-/+]+$/).default('v{version}'),
    /** Commands run in `directory`, in order, after checkout. */
    buildCommands: z.array(z.string().min(1).max(500)).max(20).default([]),
    restartCommand: z.string().max(500).default(''),
    healthcheckUrl: z.string().url().or(z.literal('')).default(''),
    healthTimeoutSeconds: z.number().int().min(5).max(900).default(120),
    /** Reverse proxy managed on the server when connecting domains. */
    proxy: z.enum(['none', 'caddy', 'nginx']).default('none'),
    appPort: z.number().int().min(1).max(65535).nullable().default(null),
    /** Contact e-mail for Let's Encrypt (nginx + certbot). */
    tlsEmail: z.string().email().or(z.literal('')).default(''),
    /** Environment file on the server: only variable NAMES are read, never values (F12). */
    envFile: z.string().max(300).regex(/^(\/[\w@%+=:,./-]+)?$/, 'Chemin absolu sans espace ni caractère spécial').default(''),
});
export const deployRequestSchema = z.object({
    version: z.string().min(1).max(80).regex(shellSafe, 'Version invalide'),
    service: z.string().max(80).nullable().optional(),
    preBackup: z.boolean().default(true),
    /** Must equal `<project-slug>/<environment-name>` for production. */
    typedConfirmation: z.string().optional(),
    /** Redeploys a previous ref without changelog gate only for an explicit rollback to an already-deployed version. */
    rollbackOf: z.string().uuid().optional(),
});
const hostnameRe = /^(?=.{1,253}$)(?!-)([a-z0-9-]{1,63}\.)+[a-z]{2,63}$/i;
export const domainCreateSchema = z.object({
    environmentId: z.string().uuid(),
    connectionId: z.string().uuid(),
    zoneId: z.string().min(1).max(64),
    zoneName: z.string().regex(hostnameRe),
    /** Full hostname, e.g. app.exemple.com or exemple.com. */
    hostname: z.string().regex(hostnameRe, 'Nom de domaine invalide'),
    recordType: z.enum(['A', 'AAAA', 'CNAME']),
    target: z.string().min(1).max(253),
    proxied: z.boolean().default(true),
    /** Also write the reverse-proxy configuration on the deploy server. */
    configureServer: z.boolean().default(false),
});
export const alertRuleSchema = z.object({
    projectId: z.string().uuid().nullable(),
    eventTypes: z.array(z.string()).min(1),
    minSeverity: z.enum(['info', 'warning', 'critical']).default('warning'),
    channels: z.array(z.enum(['in_app', 'email'])).default([]),
    recipientUserIds: z.array(z.string().uuid()).default([]),
    /** Notification connections (Slack, Discord, SMTP, webhook…). */
    connectionIds: z.array(z.string().uuid()).default([]),
    escalateAfterMinutes: z.number().int().min(5).max(24 * 60).nullable().default(null),
});
// --- F11 Sécurité ------------------------------------------------------------------
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date AAAA-MM-JJ');
/** Triage of a finding. Justification is mandatory for false positives and accepted risks. */
export const findingTriageSchema = z
    .object({
    status: z.enum(FINDING_STATUSES),
    justification: z.string().max(2000).default(''),
    reviewAt: isoDate.nullable().default(null),
    assigneeId: z.string().uuid().nullable().optional(),
})
    .superRefine((v, ctx) => {
    if ((v.status === 'false_positive' || v.status === 'accepted') && v.justification.trim().length < 10)
        ctx.addIssue({ code: 'custom', path: ['justification'], message: 'Justification obligatoire (10 caractères minimum).' });
    if (v.status === 'accepted' && !v.reviewAt)
        ctx.addIssue({ code: 'custom', path: ['reviewAt'], message: 'Date de réexamen obligatoire pour un risque accepté.' });
});
// --- F10 Bugs et incidents --------------------------------------------------------
/** Error event pushed by an application or a log forwarder (signed webhook). */
export const errorEventSchema = z.object({
    environment: z.string().min(1).max(40),
    message: z.string().min(1).max(4000),
    /** Exception type, e.g. TypeError. */
    type: z.string().max(200).default(''),
    /** Top frame or route, e.g. "apps/web/src/api/orders.ts in createOrder". */
    culprit: z.string().max(500).default(''),
    level: z.enum(ERROR_LEVELS).default('error'),
    version: z.string().max(80).nullable().optional(),
    /** Optional grouping key; computed from type + culprit + normalised message otherwise. */
    fingerprint: z.string().max(200).optional(),
    count: z.number().int().min(1).max(1_000_000).default(1),
    occurredAt: z.string().datetime().optional(),
    url: z.string().url().nullable().optional(),
    service: z.string().max(80).nullable().optional(),
});
export const errorGroupUpdateSchema = z.object({
    status: z.enum(ISSUE_STATUSES).optional(),
    assigneeId: z.string().uuid().nullable().optional(),
    ticketUrl: httpUrl.or(z.literal('')).nullable().optional(),
    impact: z.string().max(2000).optional(),
    incidentId: z.string().uuid().nullable().optional(),
});
export const incidentCreateSchema = z.object({
    title: z.string().min(3).max(200),
    severity: z.enum(INCIDENT_SEVERITIES).default('major'),
    summary: z.string().max(5000).default(''),
    environmentIds: z.array(z.string().uuid()).max(20).default([]),
    componentIds: z.array(z.string().uuid()).max(50).default([]),
    startedAt: z.string().datetime().optional(),
    ownerId: z.string().uuid().nullable().default(null),
    ticketUrl: httpUrl.or(z.literal('')).default(''),
    errorGroupIds: z.array(z.string().uuid()).max(50).default([]),
});
export const incidentUpdateSchema = patchSchema(incidentCreateSchema.omit({ errorGroupIds: true })).extend({
    status: z.enum(ISSUE_STATUSES).optional(),
    postmortem: z.string().max(50_000).optional(),
});
export const incidentEventSchema = z.object({
    kind: z.enum(INCIDENT_EVENT_KINDS).exclude(['status', 'detection']).default('note'),
    body: z.string().min(1).max(5000),
    at: z.string().datetime().optional(),
});
// --- F12 Variables d'environnement -------------------------------------------------
const varName = z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,127}$/, 'Nom de variable invalide');
/** Variables declared by hand for an environment (names only, never values). */
export const envVariablesDeclareSchema = z.object({
    names: z.array(varName).max(500),
    /** Variables the application does not need in this environment. */
    ignored: z.array(varName).max(500).default([]),
});
// --- Tests de restauration planifiés ----------------------------------------------
/** Periodic restore of the latest backup onto an isolated, non-production target. */
export const restoreTestSchema = z.object({
    policyId: z.string().uuid(),
    sourceComponentId: z.string().uuid(),
    targetComponentId: z.string().uuid(),
    everyDays: z.number().int().min(1).max(90).default(7),
    /** Warnings (extra objects on the target, unverified compatibility) are accepted for a test target. */
    acceptWarnings: z.boolean().default(true),
    enabled: z.boolean().default(true),
});
//# sourceMappingURL=schemas.js.map